<template>
	<header>
		<div class="bg-color-content-primary pb-200 pt-300">
			<div class="hero-container-xl">
				<div :class="['container pt-100 pb-300', { 'pb-600-lg': !thirdPartyAuthStore.isSignupEnabled }]">
					<div
						class="col-6-md col-offset-3-md text-color-white text-align-center display-flex flex-direction-column align-items-center"
					>
						<img :src="logoImage" alt="Fundrise Flagship Fund" style="width: 170px" />

						<h1 class="mt-200 heading-2 heading-1-md">
							Invest in
							<BasePlaceHolderInline width="12rem" :placehold="!fundAumLabel" theme="dark">{{
								fundAumDisplayLabel
							}}</BasePlaceHolderInline>
							of<br />real estate
						</h1>

						<p>
							Consistent growth over time is a timeless — if not trendy — way to build real wealth. Our
							{{ fundAumDisplayLabel }}* real estate fund aims to give your portfolio a new level of
							balance, growing your net worth for decades to come.
						</p>
						<ul class="icon-data-list pt-200-md pt-300 justify-content-center col-9 col-12-md">
							<li v-for="data in iconDataList" :key="data.icon" class="display-flex gap-100">
								<BaseSvgIcon :name="data.icon" width="24" height="24" />
								<span class="body-md col-9-md text-align-left" v-html="data.label"></span>
							</li>
						</ul>
					</div>
					<div
						class="col-6-md col-offset-3-md text-color-white text-align-center display-flex flex-direction-column align-items-center"
					>
						<slot name="lead-capture"> </slot>
					</div>
				</div>
			</div>
			<img v-if="appStore.isMobile" data-test="mobile-image" :src="mobileImage" alt="" role="presentation" />
			<div class="container">
				<div class="col-8-md col-offset-2-md text-align-center pt-200 pt-100-md pb-200-smo body-xs muted">
					<p
						>*Net value of the fund's assets less its liabilities as of
						<BasePlaceHolderInline width="3rem" :placehold="!fundAumDate" theme="dark">{{
							fundAumDisplayDate
						}}</BasePlaceHolderInline
						>.</p
					>
				</div>
			</div>
		</div>
	</header>
</template>

<script setup lang="ts">
import { appCloudfrontPath } from '@constants';
import { computed } from 'vue';
import { CustomSpacetimeFormat } from 'types/spacetime-formats';
import spacetime from 'spacetime';
import { useAppStore } from '@stores/app';
import { useThirdPartyAuthStore } from '@stores/third-party-auth';

interface Props {
	fundAumLabel: string;
	fundAumDate: string;
}

const props = defineProps<Props>();

const appStore = useAppStore();
const thirdPartyAuthStore = useThirdPartyAuthStore();

const mobileImage = `${appCloudfrontPath}/images/marketing-site/funds/flagship-fund-collage-mobile-cropped.png`;
const logoImage = `${appCloudfrontPath}/images/global/fundrise-flagship-logo-white.svg`;

const iconDataList = [
	{
		icon: 'flagship-buildforrent',
		label: '4,700+ single-family rental homes'
	},
	{
		icon: 'performance-outline',
		label: 'Flexible minimums starting at just $10'
	},
	{
		icon: 'flagship-industrialproperties',
		label: '3.3+ million square feet of industrial facilities'
	},
	{
		icon: 'invest',
		label: 'Quarterly, penalty-free withdrawals'
	}
];

const fundAumDisplayLabel = computed(() => {
	return props.fundAumLabel.replace('B', ' billion');
});

const fundAumDisplayDate = computed(() => {
	return props.fundAumDate && spacetime(props.fundAumDate).format(CustomSpacetimeFormat.MONTH_SHORT_DATE_YEAR);
});
</script>
<style lang="scss" scoped>
@use '../../../styles/constants/layout' as *;

.icon-data-list {
	row-gap: 1rem;
	display: grid;
	align-items: start;

	@media screen and (min-width: $md-start) {
		grid-template-columns: repeat(2, 1fr);
	}

	@media screen and (max-width: $md-start) {
		> li:nth-child(2) {
			order: 3;
		}

		> li:nth-child(4) {
			order: 4;
		}
	}
}

.hero-container-xl {
	@media screen and (min-width: 1440px) {
		width: 1440px;
		margin-left: auto;
		margin-right: auto;
	}

	@media screen and (min-width: $md-start) {
		background-image: url($cloud-front-domain + '/images/marketing-site/funds/flagship-fund-collage.png');
		background-position: 0 6rem;
		background-size: contain;
		background-repeat: no-repeat;
	}
}
</style>
